import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

export interface BillingSettings {
  taxRate: number;
  stripeFixedFee: number;
  stripePercentFee: number;
  currency: string;
}

const DEFAULT_BILLING: BillingSettings = {
  taxRate: Number(process.env.REACT_APP_TAX_RATE ?? 0.21),
  stripeFixedFee: Number(process.env.REACT_APP_STRIPE_FIXED_FEE ?? 0.3),
  stripePercentFee: Number(process.env.REACT_APP_STRIPE_PERCENT_FEE ?? 0.035),
  currency: String(process.env.REACT_APP_CURRENCY ?? 'EUR'),
};

export const useBilling = () => {
  const [billing, setBilling] = useState<BillingSettings>(DEFAULT_BILLING);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ref = doc(db, 'settings', 'game');

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (!snapshot.exists()) {
          setBilling(DEFAULT_BILLING);
          setLoading(false);
          return;
        }

        const data = snapshot.data();

        setBilling({
          taxRate: Number(data.taxRate ?? DEFAULT_BILLING.taxRate),
          stripeFixedFee: Number(
            data.stripeFixedFee ?? DEFAULT_BILLING.stripeFixedFee,
          ),
          stripePercentFee: Number(
            data.stripePercentFee ?? DEFAULT_BILLING.stripePercentFee,
          ),
          currency: String(data.currency ?? DEFAULT_BILLING.currency),
        });

        setLoading(false);
      },
      () => {
        setBilling(DEFAULT_BILLING);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const helpers = useMemo(() => {
    const round2 = (value: number) => Math.round(value * 100) / 100;

    const calculate = (grossValue: number) => {
      const gross = round2(grossValue);

      // Base imponible (sin IVA)
      const taxable = round2(gross / (1 + billing.taxRate));

      // IVA repercutido
      const tax = round2(gross - taxable);

      // Comisión real Stripe sobre el total cobrado
      const stripeFee = round2(
        gross * billing.stripePercentFee + billing.stripeFixedFee,
      );

      // Lo que realmente te queda
      const net = round2(taxable - stripeFee);

      return {
        gross, // total pagado por cliente
        taxable, // base imponible
        taxRate: billing.taxRate, // 0.21
        tax, // IVA en €
        stripePercentFee: billing.stripePercentFee,
        stripeFixedFee: billing.stripeFixedFee,
        stripeFee, // comisión total Stripe €
        net, // neto real
      };
    };

    return {
      calculate,
    };
  }, [billing]);

  return {
    billing,
    loading,
    ...helpers,
  };
};
